import { Button } from '@/components/elements/forms/buttons';
import Icon from '@/components/icons/Icon';
import FakeSearchInput from '@/components/modules/mobile/FakeSearchInput';
import { formatSelectedRange } from '@/features/date-picker/helpers';
import Toggle from '@/features/deals/components/Toggle';
import { isRedirectedFromPreview, useSmtOnlyToggle } from '@/features/deals/hooks/useSmtOnlyToggle';
import { buildSerpOrSokLink } from '@/features/mega-menu/helpers';
import { classnames } from '@/helpers';
import { DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION } from '@/hooks/search/useSearch.constants';
import { isDealsSearch, SearchType, searchTypeSchema } from '@/hooks/search/useSearch.helpers';
import { _s, _t } from '@/locale';
import { autocompleteService } from '@/services';
import { urlSearchStateSchema } from '@/types/state/search';
import { Fragment, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import KeywordAutoSuggest from '../../components/AutoSuggest/KeywordAutoSuggest/KeywordAutoSuggest';
import useKeywordAutoSuggestManager from '../../components/AutoSuggest/KeywordAutoSuggest/KeywordAutoSuggest.hooks';
import LocationAutosuggest from '../../components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest';
import useLocationAutoSuggestManager from '../../components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.hooks';
import { LocationSuggestion } from '../../components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.types';
import {
  AutoSuggestInputComponent,
  KeywordAutoSuggestSectionTitle,
  KeywordSuggestionItem,
  LocationAutoSuggestSectionTitle,
  LocationSuggestionItem,
} from '../../components/AutoSuggest/shared/templates';
import { AutoSuggestInputTheme } from '../../components/AutoSuggest/shared/types';
import DateTimePicker from '../../components/DateTimePicker/DateTimePicker';
import useDateTimePickerManager from '../../components/DateTimePicker/DateTimePicker.hooks';
import { SearchContext } from '../../types/tracking';
import { getTimeOfDateFilterLabel } from '../index.helpers';

const AUTOGUSGEST_THEME: AutoSuggestInputTheme = {
  container: 'relative w-full',
  suggestionsContainer: 'absolute space-y-lg w-full drop-shadow-xs',
  suggestionsContainerOpen: 'bg-white p-sm mt-xs rounded-md',
  suggestionsList: 'space-y-sm',
  suggestionHighlighted: 'bg-surface_l4 rounded-md',
};

const HEADER_HEIGHT = 56;

const baseTranslationKey = 'features.searchV2.pages.search.SearchPage';

function getSearchFiltersFromSearchQuery(searchQuery: string) {
  const searchParams = new URLSearchParams(searchQuery);
  const params = {};
  searchParams.forEach((value, key) => {
    params[key] = value;
  });
  const validStartDate = urlSearchStateSchema.safeParse({ startDate: params['startDate'] }).success;
  const validEndDate = urlSearchStateSchema.safeParse({ endDate: params['endDate'] }).success;
  const validTimeOfDay = urlSearchStateSchema.safeParse({ timeOfDay: params['timeOfDay'] }).success;

  return {
    startDate: validStartDate ? params['startDate'] : undefined,
    endDate: validEndDate ? params['endDate'] : undefined,
    timeOfDay: validTimeOfDay ? params['timeOfDay'] : undefined,
    lat: params['lat'] || undefined,
    lon: params['lon'] || undefined,
  };
}

const SearchPage = () => {
  const location = useLocation<{
    searchContext?: SearchContext;
    searchType?: unknown;
    q?: string;
    location?: string;
    fromLandingPage?: boolean;
  }>();
  const history = useHistory();
  const keywordInputRef = useRef(null);
  const locationInputRef = useRef(null);
  const searchFilters = getSearchFiltersFromSearchQuery(location.search);
  const [searchContext, setSearchContext] = useState<SearchContext>(location?.state?.searchContext ?? undefined);
  const { data, success } = searchTypeSchema.safeParse(location?.state?.searchType);
  const searchType = success ? data : 'default';
  const redirectedFromPreview = isRedirectedFromPreview(location?.state);
  const { isSmtOnlySearch, setIsSmtOnlySearch } = useSmtOnlyToggle(searchType, redirectedFromPreview);

  const isSistaminutenSearch = Boolean(searchType === 'sistaminuten');

  const initialState = {
    keyword: location?.state?.q,
    location: location?.state?.location,
    lat: searchFilters.lat,
    lon: searchFilters.lon,
    startDate: searchFilters.startDate,
    endDate: searchFilters.endDate,
    timeOfDay: searchFilters.timeOfDay,
  };

  const [isOpenKeywordAutoSuggest, setIsOpenKeywordAutoSuggest] = useState<boolean>(!initialState.keyword);
  const [isOpenLocationAutoSuggest, setIsOpenLocationAutoSuggest] = useState<boolean>(false);
  const [isOpenDateTimePicker, setIsOpenDateTimePicker] = useState<boolean>(false);

  const keywordAutoSuggestManager = useKeywordAutoSuggestManager({
    initialState: {
      keyword: initialState.keyword ?? '',
      suggestions: [],
      storedSuggestions: [],
      newServices: [],
      hasHistory: false,
      showSuggestions: true,
    },
    searchType,
    onSelectKeywordSuggestion: handleOnSelectKeywordSuggestion,
  });

  const locationAutoSuggestManager = useLocationAutoSuggestManager({
    initialState: {
      locationValue: initialState.location ?? '',
      lat: initialState.lat,
      lon: initialState.lon,
      hasHistory: false,
      suggestions: [],
      storedSuggestions: [],
      showSuggestions: true,
      isFetchingUserPosition: false,
    },
    loadFromHistory: !isSistaminutenSearch,
    onSelectLocationSuggestion: handleOnSelectLocationSuggestion,
  });

  const dateTimePickerManager = useDateTimePickerManager({
    initialState: {
      startDate: initialState.startDate,
      endDate: initialState.endDate,
      timeOfDay: initialState.timeOfDay,
      showCalendar: true,
    },
    onUpdateDateTime: handleOnUpdateDateTime,
    onClearDateTime: handleOnClearDateTime,
  });

  function handleOnUpdateDateTime() {
    setIsOpenDateTimePicker(false);
  }
  function handleOnClearDateTime() {
    dateTimePickerManager.onChangeDateTime({ startDate: null, endDate: null, timeOfDay: null });
    setIsOpenDateTimePicker(true);
  }

  function handleOnSelectKeywordSuggestion() {
    if (location?.state?.fromLandingPage) {
      setSearchContext('home_page_search_autocomplete');
    } else {
      setSearchContext('search_autocomplete');
    }

    setIsOpenKeywordAutoSuggest(false);
  }

  function handleOnSelectLocationSuggestion() {
    setIsOpenLocationAutoSuggest(false);
  }

  function handleOpenKeywordAutoSuggest() {
    setIsOpenKeywordAutoSuggest(true);
  }

  function handleOnCloseKeywordSuggestion() {
    setIsOpenKeywordAutoSuggest(false);
  }

  function handleOpenLocationAutoSuggest() {
    setIsOpenLocationAutoSuggest(true);
  }

  function handleOnCloseLocationSuggestion() {
    setIsOpenLocationAutoSuggest(false);
  }

  function handleOpenDateTimePicker() {
    setIsOpenDateTimePicker(true);
  }

  function handleOnCloseDateTimeSuggestion() {
    setIsOpenDateTimePicker(false);
  }

  function handleOnClearKeyword() {
    keywordAutoSuggestManager.onChange(null, { newValue: '' });
    setSearchContext(undefined);
    setIsOpenKeywordAutoSuggest(true);
    keywordInputRef?.current?.focus?.();
  }

  function handleOnClearLocation() {
    locationAutoSuggestManager.onChange({ target: { value: '' } });
    setIsOpenLocationAutoSuggest(true);
    locationInputRef?.current?.focus?.();
  }

  function handleOnBlurLocationAutoSuggest(event, suggestion) {
    locationAutoSuggestManager.onBlur(event, suggestion);
  }

  function handleOnBlurKeywordAutoSuggest(event, suggestion) {
    keywordAutoSuggestManager.onBlur(event, suggestion);
  }

  function handleOnAutoSuggestKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Enter' || event.keyCode === 13) {
      setIsOpenLocationAutoSuggest(false);
      setIsOpenKeywordAutoSuggest(false);
    }
  }

  function handleRemoveLocationHistorySuggestion(suggestion: LocationSuggestion) {
    autocompleteService.deleteLocationSuggestion(suggestion.label);
    locationAutoSuggestManager.onClearHistorySuggestion(suggestion);
  }

  function handlePerformSearch() {
    const state = {
      keyword: keywordAutoSuggestManager.keyword || DEFAULT_SEARCH_KEYWORD,
      location: locationAutoSuggestManager.location || DEFAULT_SEARCH_LOCATION,
    };

    if (keywordAutoSuggestManager.keyword) {
      keywordAutoSuggestManager.saveKeywordToHistory(keywordAutoSuggestManager.keyword);
    }

    const { startDate, endDate, timeOfDay } = dateTimePickerManager;
    const query = new URLSearchParams(location.search);
    /**
     * override existing query params for dateTime filters.
     * if they are set to null then they are cleared and
     * should be removed from the query string.
     */
    startDate ? query.set('startDate', startDate) : query.delete('startDate');

    if (startDate && endDate) {
      query.set('endDate', endDate);
    } else {
      query.delete('endDate');
    }

    timeOfDay ? query.set('timeOfDay', timeOfDay) : query.delete('timeOfDay');

    /**
     * Override existing query params for lat/lon
     */
    const { lat, lon } = locationAutoSuggestManager;

    if (lat && lon) {
      query.set('lat', lat);
      query.set('lon', lon);
    } else {
      query.delete('lat');
      query.delete('lon');
    }

    const { pathname, searchString } = buildSerpOrSokLink(
      state.keyword,
      state.location,
      searchType,
      query.toString(),
      redirectedFromPreview,
    );

    /**
     * if keyword input is dirty, the 'searchContext' state is set
     * otherwise if it is not dirty, or have been cleared then we set the searchContext to 'free_search'
     * or 'home_page_free_search' if the user is coming from the landing page
     */
    let _searchContext: SearchContext = undefined;

    if (searchContext) {
      _searchContext = searchContext;
    } else if (location?.state?.fromLandingPage) {
      _searchContext = 'home_page_free_search';
    } else {
      _searchContext = 'free_search';
    }

    history.push({
      pathname,
      search: searchString,
      state: { keywordSubmitted: true, searchContext: _searchContext },
    });
  }

  const inputSharedClassNames = classnames(
    'border-border_regular group-hover:border-border_bold group-hover:bg-surface_l4 h-[52px]',
  );

  if (isOpenKeywordAutoSuggest) {
    return (
      <ContentWrapper searchType={searchType}>
        <HeaderWrapper>
          <button className={getNavigationButtonClassNames(searchType)} onClick={handleOnCloseKeywordSuggestion}>
            <Icon variant="back" />
          </button>
          <h2 className="text-h-s text-fg_secondary">{_s(`${baseTranslationKey}.keywordAutoSuggestModal.title`)}</h2>
        </HeaderWrapper>
        <div>
          <KeywordAutoSuggest
            inputRef={keywordInputRef}
            {...keywordAutoSuggestManager}
            onBlur={handleOnBlurKeywordAutoSuggest}
            onKeyDown={handleOnAutoSuggestKeyDown}
            theme={AUTOGUSGEST_THEME}
            renderInputComponent={({ key, ...inputProps }) => {
              return (
                <AutoSuggestInputComponent
                  key={key}
                  inputProps={{ ...inputProps, autoFocus: true }}
                  isFocused={isOpenKeywordAutoSuggest}
                  className={classnames(inputSharedClassNames, 'border-border_menu rounded-full')}
                  onClear={handleOnClearKeyword}
                  leftSlot={<Icon variant="search" />}
                />
              );
            }}
            renderSuggestion={(suggestion, options) => (
              <KeywordSuggestionItem
                suggestion={suggestion}
                options={options}
                onRemoveHistorySuggestion={keywordAutoSuggestManager.onClearHistorySuggestion}
              />
            )}
            renderSectionTitle={(section) => <KeywordAutoSuggestSectionTitle {...section} />}
          />
        </div>
      </ContentWrapper>
    );
  }

  if (isOpenLocationAutoSuggest) {
    return (
      <ContentWrapper searchType={searchType}>
        <HeaderWrapper>
          <button className={getNavigationButtonClassNames(searchType)} onClick={handleOnCloseLocationSuggestion}>
            <Icon variant="back" />
          </button>
          <h2 className="text-h-s text-fg_secondary">{_s(`${baseTranslationKey}.locationAutoSuggestModal.title`)}</h2>
        </HeaderWrapper>

        <LocationAutosuggest
          inputRef={locationInputRef}
          {...locationAutoSuggestManager}
          onBlur={handleOnBlurLocationAutoSuggest}
          onKeyDown={handleOnAutoSuggestKeyDown}
          theme={AUTOGUSGEST_THEME}
          renderInputComponent={({ key, ...inputProps }) => {
            return (
              <AutoSuggestInputComponent
                key={key}
                inputProps={{ ...inputProps, autoFocus: true }}
                isFocused={isOpenLocationAutoSuggest}
                className={classnames(inputSharedClassNames, 'border-border_menu rounded-full')}
                onClear={handleOnClearLocation}
                leftSlot={
                  <button className="flex items-center" onClick={handleOnCloseLocationSuggestion}>
                    <Icon variant="location" />
                  </button>
                }
              />
            );
          }}
          renderSuggestion={(suggestion, options) => (
            <LocationSuggestionItem
              suggestion={suggestion}
              options={options}
              onRemoveHistorySuggestion={handleRemoveLocationHistorySuggestion}
            />
          )}
          renderSectionTitle={(section) => <LocationAutoSuggestSectionTitle {...section} />}
        />
      </ContentWrapper>
    );
  }

  if (isOpenDateTimePicker) {
    return (
      <ContentWrapper searchType={searchType}>
        <HeaderWrapper>
          <button className={getNavigationButtonClassNames(searchType)} onClick={handleOnCloseDateTimeSuggestion}>
            <Icon variant="back" />
          </button>
          <h2 className="text-h-s text-fg_secondary">{_s(`${baseTranslationKey}.dateTimePickerModal.title`)}</h2>
        </HeaderWrapper>

        <div className="pb-2xl">
          <DateTimePicker
            {...dateTimePickerManager}
            onDateTimeSearch={handlePerformSearch}
            renderInputComponent={({ hasFilterApplied, onClear, ...props }) => {
              const handleOnClear = (e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onClear(e);
                dateTimePickerManager.onClearDateTime();
              };

              return (
                <div className="px-md relative flex h-[52px] items-center rounded-full border bg-white">
                  <Icon variant="calendar-event" />
                  <div className="flex w-full flex-col">
                    <span className="ml-sm text-fg_secondary text-xs">{props.placeholder}</span>
                    <input {...props} className={classnames(props.className)} />
                  </div>
                  {hasFilterApplied && (
                    <button
                      onClick={handleOnClear}
                      onMouseDown={handleOnClear}
                      className="absolute right-0 top-0 z-10 h-full !w-auto px-4">
                      <Icon variant="close-circle" />
                    </button>
                  )}
                </div>
              );
            }}
          />
        </div>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper searchType={searchType}>
      <HeaderWrapper>
        <button className={getNavigationButtonClassNames(searchType)} onClick={() => history.goBack()}>
          <Icon variant="close-alt" />
        </button>
        <h1 className={classnames('text-h-s', isDealsSearch(searchType) ? 'text-fg_primary' : 'text-fg_secondary')}>
          {_t(`${baseTranslationKey}.page-title${isDealsSearch(searchType) ? 'Deals' : ''}`)}
          {isDealsSearch(searchType) && <span className="text-fg_highlight font-semibold">Deal</span>}
        </h1>
      </HeaderWrapper>

      <div className="space-y-lg">
        <FakeSearchInput
          isLoading={false}
          onClick={handleOpenKeywordAutoSuggest}
          valid={keywordAutoSuggestManager.keyword.length > 0}
          icon={isDealsSearch(searchType) ? 'search-deal' : 'search'}
          onClear={handleOnClearKeyword}
          content={
            <span className="space-y-3xs flex h-full flex-col">
              {keywordAutoSuggestManager.keyword && (
                <span className="text-fg_secondary text-xs">{_s(`${baseTranslationKey}.keywordInput.title`)}</span>
              )}
              <span className="text-l overflow-hidden text-ellipsis whitespace-nowrap">
                {keywordAutoSuggestManager.keyword || _s(`${baseTranslationKey}.keywordInput.title`)}
              </span>
            </span>
          }
          size="md"
        />
        <FakeSearchInput
          isLoading={false}
          onClick={handleOpenLocationAutoSuggest}
          valid={locationAutoSuggestManager.location.length > 0}
          icon="location"
          onClear={handleOnClearLocation}
          content={
            <span className="space-y-3xs flex h-full flex-col">
              {locationAutoSuggestManager.location && (
                <span className="text-fg_secondary text-xs">{_s(`${baseTranslationKey}.locationInput.title`)}</span>
              )}
              <span className="text-l overflow-hidden text-ellipsis whitespace-nowrap">
                {locationAutoSuggestManager.location || _s(`${baseTranslationKey}.locationInput.title`)}
              </span>
            </span>
          }
          size="md"
        />
        {!isSistaminutenSearch && !isDealsSearch(searchType) && (
          <FakeSearchInput
            isLoading={false}
            onClick={handleOpenDateTimePicker}
            valid={Boolean(
              dateTimePickerManager.startDate || dateTimePickerManager.endDate || dateTimePickerManager.timeOfDay,
            )}
            icon="calendar-event"
            onClear={handleOnClearDateTime}
            content={
              <span className="space-y-3xs flex h-full flex-col">
                {dateTimePickerManager.startDate && (
                  <span className="text-fg_secondary text-xs">{_s(`${baseTranslationKey}.dateTimeInput.title`)}</span>
                )}
                <span className="text-l">
                  {(() => {
                    if (
                      !dateTimePickerManager.startDate &&
                      !dateTimePickerManager.endDate &&
                      !dateTimePickerManager.timeOfDay
                    ) {
                      return _s('when');
                    }

                    let startDate = null;
                    let endDate = null;

                    if (dateTimePickerManager.startDate) {
                      startDate = new Date(dateTimePickerManager.startDate);
                    }

                    if (dateTimePickerManager.endDate) {
                      endDate = new Date(dateTimePickerManager.endDate);
                    }

                    return formatSelectedRange(
                      startDate && endDate ? [startDate, endDate] : dateTimePickerManager.startDate ? [startDate] : [],
                      new Date(),
                    );
                  })()}

                  {dateTimePickerManager.timeOfDay && (
                    <Fragment>
                      ,&nbsp;
                      {getTimeOfDateFilterLabel(dateTimePickerManager.timeOfDay)}
                    </Fragment>
                  )}
                </span>
              </span>
            }
            size="md"
          />
        )}
        {isDealsSearch(searchType) && (
          <div
            className="py-2xs px-xs flex items-center justify-between"
            onClick={() => setIsSmtOnlySearch(!isSmtOnlySearch)}>
            <span className="text-fg_primary text-l">{_t('searchSistaminutenOnly')}</span>
            <Toggle onChange={() => setIsSmtOnlySearch(!isSmtOnlySearch)} checked={isSmtOnlySearch} />
          </div>
        )}
      </div>
      <div className="mt-xl">
        <Button size="lg" block onClick={handlePerformSearch} leftIcon={<Icon variant="search" />}>
          {(() => {
            if (isDealsSearch(searchType)) {
              if (isSmtOnlySearch) {
                return `${_t('search')} Sista Minuten`;
              } else {
                return _t('searchDiscountedTimes');
              }
            }

            if (isSistaminutenSearch) {
              return `${_t('search')} Sista Minuten`;
            }

            return _t('search');
          })()}
        </Button>

        {isSistaminutenSearch && (
          <p className="text-m py-xs text-center">{_t(`${baseTranslationKey}.sistaminutenSearchDescription`)}</p>
        )}
      </div>
    </ContentWrapper>
  );
};

function getNavigationButtonClassNames(searchType: SearchType) {
  return classnames('p-xs absolute left-0 rounded-full', !isDealsSearch(searchType) && 'bg-bg_secondary_bold');
}

const ContentWrapper = ({ searchType, children }: { searchType: SearchType; children: React.ReactNode }) => {
  return (
    <div
      className={classnames('px-md bg-surface_l4 min-h-[100vh]', isDealsSearch(searchType) && 'bg-gradient-deals')}
      onMouseDown={(e) => e.preventDefault()}>
      {children}
    </div>
  );
};

const HeaderWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <header className="mb-sm relative flex items-center justify-center" style={{ height: HEADER_HEIGHT }}>
      {children}
    </header>
  );
};

export default SearchPage;
